import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList, CATEGORIES, CATEGORY_HERO } from "@/mocks/anime";

export default function Library() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const search = params.get("search") || "";

  const filtered = useMemo(() => {
    let list = animeList;
    if (cat !== "all") list = list.filter((a) => a.category === cat);
    if (search) {
      const kw = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) || a.tag.toLowerCase().includes(kw)
      );
    }
    return list;
  }, [cat, search]);

  useEffect(() => {
    const hero = CATEGORY_HERO[cat];
    document.title = hero
      ? `${hero.title} - 樱花看番番剧库`
      : "番剧库 - 樱花看番";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        hero
          ? `${hero.title}专区，${hero.desc} 樱花看番番剧库在线观看。`
          : "浏览樱花看番番剧库，动画、剧场版、OVA、泡面番与国漫全类型在线观看。"
      );
    }
  }, [cat]);

  const setCat = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("cat");
    else next.set("cat", key);
    next.delete("search");
    setParams(next, { replace: true });
  };

  const hero = CATEGORY_HERO[cat];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* page header */}
        <section className="relative flex h-60 items-end overflow-hidden md:h-72">
          <img
            src={hero ? hero.image : "https://readdy.ai/api/search-image?query=colorful%20anime%20collage%20of%20various%20genres%20with%20warm%20cinematic%20lighting%2C%20stylized%20background&width=1600&height=600&seq=library-hero&orientation=landscape"}
            alt={`${hero ? hero.title : "番剧库"} 背景`}
            title="樱花看番番剧库"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-950/40 to-background-950/30" />
          <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6 pb-8">
            <nav className="mb-3 flex items-center gap-1 text-xs text-foreground-600">
              <Link to="/" className="hover:text-primary-300">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-400">番剧库</span>
            </nav>
            <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">
              {hero ? hero.title : "番剧库 · 全收录"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600 md:text-base">
              {hero ? hero.desc : "动画、剧场版、OVA、泡面番与国漫，点开即看全集。"}
            </p>
          </div>
        </section>

        {/* filter bar */}
        <div className="sticky top-16 z-30 border-b border-background-200/60 bg-background-50/90 backdrop-blur-md md:top-20">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCat(c.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    cat === c.key
                      ? "bg-primary-500 text-background-950"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={c.icon} />
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* results */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-sm text-foreground-600">
              共 <span className="font-medium text-accent-300">{filtered.length}</span>{" "}
              部{search ? ` · 关键词「${search}」` : ""}
            </h2>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {filtered.map((a) => (
                <AnimeCard key={a.id} anime={a} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300/60 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-search-line text-2xl" />
              </span>
              <p className="mt-4 font-medium text-foreground-700">没有找到相关番剧</p>
              <p className="mt-1 text-sm text-foreground-600">
                换个关键词或切换番剧类型再试试
              </p>
              <button
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
              >
                查看全部番剧
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}